import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, Trash2, AlertTriangle, Loader2, ArrowLeft, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/useToast';
import { useFS } from '@/hooks/useFS';
import { useIsMobile } from '@/hooks/useIsMobile';
import { isPersistentStorageSupported, isPersistentStorageGranted, requestPersistentStorage, getStorageInfo } from '@/lib/persistentStorage';
import JSZip from 'jszip';

interface StorageInfo {
  quota: number;
  usage: number;
}

export function StorageSettings() {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isRequestingPersistent, setIsRequestingPersistent] = useState(false);
  const [storageInfo, setStorageInfo] = useState<StorageInfo | null>(null);
  const [storageError, setStorageError] = useState<string | null>(null);
  const [isPersistent, setIsPersistent] = useState<boolean | null>(null);
  const { toast } = useToast();
  const { fs } = useFS();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  useEffect(() => {
    const loadStorageInfo = async () => {
      try {
        if (!isPersistentStorageSupported()) {
          setStorageError('StorageManager API is not available in this browser');
          return;
        }
        const info = await getStorageInfo();
        if (info) setStorageInfo({ quota: info.quota, usage: info.usage });
        const persistent = await isPersistentStorageGranted();
        setIsPersistent(persistent);
      } catch (error) {
        console.error('Failed to get storage information:', error);
        setStorageError(error instanceof Error ? error.message : 'Failed to get storage information');
      }
    };
    loadStorageInfo();
  }, []);

  const exportFilesAsZip = async () => {
    const zip = new JSZip();
    let fileCount = 0;
    let dirCount = 0;

    const addToZip = async (dirPath: string, zipFolder: JSZip) => {
      try {
        const entries = await fs.readdir(dirPath, { withFileTypes: true });
        console.log(`Reading directory: ${dirPath}, found ${entries.length} entries`);

        for (const entry of entries) {
          const fullPath = dirPath === '/' ? `/${entry.name}` : `${dirPath}/${entry.name}`;
          if (entry.isDirectory()) {
            dirCount++;
            const folder = zipFolder.folder(entry.name);
            if (folder) {
              await addToZip(fullPath, folder);
            }
          } else if (entry.isFile()) {
            try {
              const fileContent = await fs.readFile(fullPath);
              zipFolder.file(entry.name, fileContent);
              fileCount++;
              console.log(`Added file to zip: ${fullPath}`);
            } catch (error) {
              console.warn(`Failed to read file ${fullPath}:`, error);
            }
          }
        }
      } catch (error) {
        console.warn(`Failed to read directory ${dirPath}:`, error);
      }
    };

    console.log('Starting ZIP export...');
    await addToZip('/', zip);
    console.log(`Finished collecting files. Total: ${fileCount} files in ${dirCount} directories`);

    console.log('Generating ZIP blob...');
    const content = await zip.generateAsync({ type: 'blob' });
    console.log(`ZIP generated successfully. Size: ${content.size} bytes`);

    const url = URL.createObjectURL(content);
    const link = document.createElement('a');
    link.href = url;
    link.download = `shakespeare-projects-${new Date().toISOString().split('T')[0]}.zip`;
    document.body.appendChild(link);
    console.log('Triggering download...');
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    console.log('Download triggered successfully');
  };

  const handleExportFiles = async () => {
    setIsExporting(true);
    try {
      await exportFilesAsZip();
      toast({ title: t('filesExportedSuccessfully'), description: t('filesExportedDescription') });
    } catch (error) {
      console.error('Failed to export files:', error);
      toast({ title: t('failedToExportFiles'), description: error instanceof Error ? error.message : t('error'), variant: "destructive" });
    } finally {
      setIsExporting(false);
    }
  };

  const handlePersistentStorageToggle = async (checked: boolean) => {
    if (!checked) return;
    setIsRequestingPersistent(true);
    try {
      if (!isPersistentStorageSupported()) {
        toast({ title: t('persistentStorageNotSupported'), description: t('persistentStorageNotSupportedDescription'), variant: "destructive" });
        return;
      }
      const alreadyPersistent = await isPersistentStorageGranted();
      if (alreadyPersistent) {
        setIsPersistent(true);
        toast({ title: t('persistentStorageAlreadyGranted'), description: t('persistentStorageAlreadyGrantedDescription') });
        return;
      }
      const granted = await requestPersistentStorage();
      if (granted) {
        setIsPersistent(true);
        toast({ title: t('persistentStorageGranted'), description: t('persistentStorageGrantedDescription') });
      } else {
        toast({ title: t('persistentStorageDenied'), description: t('persistentStorageDeniedDescription'), variant: "destructive" });
      }
    } catch (error) {
      console.error('Failed to request persistent storage:', error);
      toast({ title: t('failedToRequestPersistentStorage'), description: error instanceof Error ? error.message : t('error'), variant: "destructive" });
    } finally {
      setIsRequestingPersistent(false);
    }
  };

  const handleClearAllData = async () => {
    setIsClearing(true);
    try {
      localStorage.clear();
      const databases = await indexedDB.databases();
      await Promise.all(databases.map(db => db.name ? new Promise<void>((resolve, reject) => {
        const deleteReq = indexedDB.deleteDatabase(db.name!);
        deleteReq.onsuccess = () => resolve();
        deleteReq.onerror = () => reject(deleteReq.error);
        deleteReq.onblocked = () => resolve();
      }) : Promise.resolve()));
      toast({ title: t('dataClearedSuccessfully'), description: t('dataClearedDescription') });
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      console.error('Failed to clear data:', error);
      toast({ title: t('failedToClearData'), description: error instanceof Error ? error.message : t('error'), variant: "destructive" });
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div className="p-6 space-y-8">
      {isMobile && (
        <div className="space-y-1">
          <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} className="h-8 px-2 -ml-2 text-muted-foreground">
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t('settings')}
          </Button>
        </div>
      )}

      <div>
        <h1 className="text-xl font-semibold tracking-tight">{t('storageSettings')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('dataSettingsDescriptionLong')}</p>
      </div>

      <div className="space-y-6">
        {/* Storage Usage */}
        {storageError ? (
          <p className="text-sm text-muted-foreground">{storageError}</p>
        ) : storageInfo ? (
          <div className="space-y-3">
            <h3 className="text-sm font-medium">{t('storageUsage')}</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{t('used')}: {formatBytes(storageInfo.usage)}</span>
                <span>{t('available')}: {formatBytes(storageInfo.quota)}</span>
              </div>
              <Progress value={storageInfo.quota > 0 ? (storageInfo.usage / storageInfo.quota) * 100 : 0} className="h-2" />
              <p className="text-xs text-muted-foreground text-center">
                {storageInfo.quota > 0 ? t('usagePercentage', { percentage: ((storageInfo.usage / storageInfo.quota) * 100).toFixed(1) }) : t('usageUnavailable')}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">{t('loadingStorageInfo')}</span>
          </div>
        )}

        {/* Persist Data */}
        <div className="space-y-3 border rounded-lg p-4 bg-card">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="flex-1 space-y-1">
              <h3 className="text-sm font-medium">{t('persistData')}</h3>
              <p className="text-sm text-muted-foreground">{t('persistDataDescription')}</p>
            </div>
            <Switch checked={isPersistent === true} onCheckedChange={handlePersistentStorageToggle} disabled={isRequestingPersistent || isPersistent === null} />
          </div>
        </div>

        {/* Export Files */}
        <div className="space-y-3 border rounded-lg p-4 bg-card">
          <div className="flex items-start gap-3">
            <Download className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="flex-1 space-y-1">
              <h3 className="text-sm font-medium">{t('exportFiles')}</h3>
              <p className="text-sm text-muted-foreground">{t('exportFilesDescription')}</p>
            </div>
          </div>
          <Button onClick={handleExportFiles} disabled={isExporting} size="sm" variant="outline">
            {isExporting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('exporting')}</> : <><Download className="h-4 w-4 mr-2" />{t('exportAllFiles')}</>}
          </Button>
        </div>

        {/* Clear All Data */}
        <div className="space-y-3 border border-destructive/20 rounded-lg p-4 bg-card">
          <div className="flex items-start gap-3">
            <Trash2 className="h-5 w-5 text-destructive mt-0.5" />
            <div className="flex-1 space-y-1">
              <h3 className="text-sm font-medium text-destructive">{t('clearAllData')}</h3>
              <p className="text-sm text-muted-foreground">{t('clearAllDataDescription')}</p>
            </div>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" disabled={isClearing} size="sm">
                {isClearing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('clearing')}</> : <><Trash2 className="h-4 w-4 mr-2" />{t('clearAllData')}</>}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  {t('areYouSure')}
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-2">
                  <p>{t('clearDataWarning')}</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>{t('allProjects')}</li>
                    <li>{t('aiSettingsAndKeys')}</li>
                    <li>{t('gitCredentialsSettings')}</li>
                    <li>{t('userPreferences')}</li>
                    <li>{t('cachedData')}</li>
                  </ul>
                  <p className="font-medium text-destructive">{t('actionCannotBeUndone')}</p>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={handleClearAllData} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">{t('yesClearAllData')}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}

export default StorageSettings;
